import React, { useCallback, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  mergeApplication,
  selectCreditApplication,
} from 'state/creditApplication';
import * as Yup from 'yup';
import CardChoiceView from './CardChoiceView';
import FmcForm from 'components/FmcForm';
import { RouterProps } from 'react-router';
import useNavigationHelpers from 'hooks/useNavigationHelpers';
import { selectTenant } from 'state/tenant';
import useWorkflow, { Step } from 'hooks/useWorkflow';
import { useSecureQueryParameters } from 'hooks/useQueryParameters';
import { find, propEq } from 'ramda';
import LoadingPage from 'components/LoadingPage';
import useAnalyticVariables from 'hooks/useAnalyticVariables';
import useTranslate from 'hooks/useTranslate/useTranslate';

export interface FormValues {
  cardChoice: number | null;
}

const schema = Yup.object().shape({
  cardChoice: Yup.number()
    .nullable()
    .required('required'),
});

type PropTypes = RouterProps;

const CardChoice: React.FC<PropTypes> = () => {
  const application = useSelector(selectCreditApplication);
  const {
    enabledFeatures: { cardChoices },
  } = useSelector(selectTenant);
  const dispatch = useDispatch();
  const { cardChoiceCode } = useSecureQueryParameters();
  const { setAnalyticsVariables } = useAnalyticVariables();
  const t = useTranslate();

  const cardChoiceCodeNumber = Number(cardChoiceCode);

  const { push } = useNavigationHelpers();
  const { pathAfter } = useWorkflow();

  const skipCardChoice = useMemo(
    () => find(propEq('code', cardChoiceCodeNumber), cardChoices),
    [cardChoiceCodeNumber, cardChoices],
  );

  useEffect(() => {
    if (skipCardChoice) {
      push(pathAfter(Step.CARD_CHOICE));
    }
  }, [dispatch, pathAfter, skipCardChoice, cardChoiceCodeNumber, push]);

  const onSubmit = useCallback(
    (values: FormValues) => {
      dispatch(
        mergeApplication(schema.cast({ cardChoice: values.cardChoice })),
      );
      if (values.cardChoice !== null) {
        const cardChoice = find(
          propEq('code', Number(values.cardChoice)),
          cardChoices,
        );

        if (cardChoice) {
          setAnalyticsVariables({
            selectedCardName: t(`cardChoice.choices.${cardChoice.code}.label`),
          });
        }
      }
      push(pathAfter(Step.CARD_CHOICE));
    },
    [dispatch, push, pathAfter, setAnalyticsVariables, t, cardChoices],
  );

  const initialValues: FormValues = {
    cardChoice: application.cardChoice,
  };

  if (skipCardChoice) {
    return <LoadingPage headerTitle={'Loading...'} />;
  } else {
    return (
      <FmcForm
        initialValues={initialValues}
        validationSchema={schema}
        onSubmit={onSubmit}
      >
        {props => <CardChoiceView {...props} cardChoices={cardChoices} />}
      </FmcForm>
    );
  }
};

export default CardChoice;
